import type { AxiosResponse } from 'axios';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import type ApiWorkspace from 'src/core/models/api/ApiWorkspace';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type ApiWorkspaceDetailResponse from 'src/core/models/api/responses/ApiWorkspaceDetailResponse';
import type { ApiWorkspaceResponseSettings } from 'src/core/models/api/responses/ApiWorkspacesResponse';
import { reportOrRethrow } from 'src/core/utils/ApiCallbacks';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import Translations from 'src/core/utils/Translations';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

export const useWorkspacesStore = create(
	combine(
		{
			workspaces: [] as ApiWorkspace[],
			pageSettings: null as ApiWorkspaceResponseSettings | null,
			workspacesLoading: false,
			workspacesErrorMessage: null as string | null,
			isResortPickerVisible: false,
		},
		(set, get) => {
			const fetchWorkspaces = async () => {
				if (get().workspacesErrorMessage !== null) {
					set({ workspacesErrorMessage: null });
				}

				if (get().workspaces.length === 0) {
					set({ workspacesLoading: true });
				}

				await ThermenRepository.getWorkspaces()
					.then((response) => {
						const data = response?.data ?? null;
						if (data === null) {
							set({
								workspacesErrorMessage: Translations.unknownErrorOccurred,
							});
							return;
						}

						set({ workspaces: data.data, pageSettings: data.settings });
					})
					.catch((error: ApiRequestError) => {
						const errorMessage = toErrorMessage(error);
						set({ workspacesErrorMessage: errorMessage });
					})
					.finally(() => {
						set({ workspacesLoading: false });
					});
			};

			const getApiWorkspaceDetail = async ({
				workspaceId,
				onSuccess,
				onError,
				onFinally,
			}: {
				workspaceId: number;
				onSuccess?: (response: AxiosResponse<ApiWorkspaceDetailResponse>) => void | Promise<void>;
				onError?: (error: ApiRequestError) => void;
				onFinally?: () => void;
			}) => {
				await ThermenRepository.getWorkspaceDetail(workspaceId)
					.then(async (response) => await onSuccess?.(response))
					.catch((error: ApiRequestError) => reportOrRethrow(error, onError))
					.finally(() => onFinally?.());
			};

			const getPageTitle = () => get().pageSettings?.bath_card_resorts.title ?? undefined;

			const openResortPicker = () => set({ isResortPickerVisible: true });

			const closeResortPicker = () => set({ isResortPickerVisible: false });

			return {
				fetchWorkspaces,
				getApiWorkspaceDetail,
				getPageTitle,
				openResortPicker,
				closeResortPicker,
			};
		},
	),
);
